import type { EmailAttachment } from "@/shared/types/emailSource";

export type { EmailAttachment };

/**
 * Message types the server labels raw emails with (the Pills canvas Type list). The
 * `contentBucket` filter and field carry the primary type.
 */
export const MESSAGE_CONTENT_BUCKETS = [
  "Markets",
  "RFQs",
  "TradePosts",
  "Color",
  "NewIssues",
  "Inquiries",
  "Research",
  "News",
  "PortfolioTrade",
  "Operations",
  "Curves",
  "Other",
] as const;

/** One Type/Subtype label on a message; `key` is "Type/Subtype", or just "Type". */
export interface MessageLabel {
  type: string;
  subtype: string;
  key: string;
}

export type MessageContentBucket = (typeof MESSAGE_CONTENT_BUCKETS)[number];

/** One raw-email catalog list item (GET /api/v1/raw-emails). */
export interface MessageListItem {
  id: string;
  senderIdentity: string;
  senderName: string;
  subject: string;
  sentDate: string;
  /** Primary label's type; kept for screens that predate `messageLabels`. */
  contentBucket: string;
  /** Primary label's subtype wording; kept for screens that predate `messageLabels`. */
  contentSubtype: string;
  messageType: string;
  /** Type/Subtype labels, primary first. */
  messageLabels: MessageLabel[];
  parseSucceeded: boolean;
  hasGeneratedMarketData: boolean;
  quoteCount: number;
  quoteRequestCount: number;
  occurrenceCount: number;
  additionalOccurrenceCount: number;
  bodyPreview: string;
  hasHtmlBody: boolean;
  hasAttachments: boolean;
  // Parser identity — SystemAdmin only; omitted from JSON for other clients.
  parsingStrategyUsed?: string;
  parserName?: string;
  parserVersion?: string;
  promptVersion?: string;
}

/** Paginated raw-email catalog response. */
export interface MessageSearchResponse {
  items: MessageListItem[];
  totalCount: number;
  /** True when the server stopped counting early: `totalCount` is a minimum, shown as "N+". */
  totalCountIsLowerBound?: boolean;
  offset: number;
  limit: number;
}

/** One occurrence within a deduplicated raw-email group. */
export interface MessageOccurrence {
  rawEmailStreamKey: string;
  internetMessageId: string;
  mailboxEmail: string;
  forwardedBy: string;
  forwardedByEmail: string;
  dateSent: string;
  ingestedAtUtc: string;
  isPrimary: boolean;
}

/** Raw-email catalog detail response (GET /api/v1/raw-emails/{id}). */
export interface MessageDetail {
  id: string;
  senderIdentity: string;
  senderName: string;
  subject: string;
  sentDate: string;
  /** Primary label's type; kept for screens that predate `messageLabels`. */
  contentBucket: string;
  /** Primary label's subtype wording; kept for screens that predate `messageLabels`. */
  contentSubtype: string;
  messageType: string;
  /** Type/Subtype labels, primary first. */
  messageLabels: MessageLabel[];
  parseAttempted: boolean;
  parseSucceeded: boolean;
  messageCategory: string;
  itemCount: number;
  hasGeneratedMarketData: boolean;
  quoteCount: number;
  quoteRequestCount: number;
  occurrenceCount: number;
  primaryRawEmailStreamKey: string;
  primaryInternetMessageId: string;
  securityIdentifiers: string[];
  bodyHtml: string;
  bodyPlainText: string;
  attachments: EmailAttachment[];
  occurrences: MessageOccurrence[];
}

/** Messages list filters; synced to URL via msg* query params on the Markets tab. */
export interface MessageFilters {
  q?: string;
  contentBuckets?: string[];
  sender?: string;
  sentFrom?: string;
  sentTo?: string;
  sortBy?: string;
  sortDesc?: boolean;
}

export const DEFAULT_MESSAGE_FILTERS: MessageFilters & { sortBy: string; sortDesc: boolean } = {
  sortBy: "SentDate",
  sortDesc: true,
};
